const INDENT = "  ";

const colors = {
  declaration: "#7f7f7f",
  comment: "#7f7f7f",
  tag: "#1e517b",
  attributeName: "#a93d3d",
  attributeValue: "#0b6a3a",
} as const;

export function formatXml(xml: string): string {
  const trimmed = xml.trim();
  if (trimmed === "") {
    return "";
  }
  const document = new DOMParser().parseFromString(trimmed, "application/xml");
  if (document.getElementsByTagName("parsererror").length > 0) {
    return trimmed;
  }
  const lines: string[] = [];
  const declaration = /^<\?xml[^?]*\?>/.exec(trimmed);
  if (declaration) {
    lines.push(declaration[0]);
  }
  Array.from(document.childNodes).forEach((node) => appendNode(node, 0, lines));
  return lines.join("\n");
}

function appendNode(node: Node, depth: number, lines: string[]): void {
  const pad = INDENT.repeat(depth);
  switch (node.nodeType) {
    case Node.TEXT_NODE: {
      const text = node.nodeValue?.trim() ?? "";
      if (text !== "") {
        lines.push(pad + escapeXmlText(text));
      }
      return;
    }
    case Node.CDATA_SECTION_NODE:
      lines.push(`${pad}<![CDATA[${node.nodeValue ?? ""}]]>`);
      return;
    case Node.COMMENT_NODE:
      lines.push(`${pad}<!--${node.nodeValue ?? ""}-->`);
      return;
    case Node.PROCESSING_INSTRUCTION_NODE: {
      const instruction = node as ProcessingInstruction;
      lines.push(`${pad}<?${instruction.target} ${instruction.data}?>`);
      return;
    }
    case Node.ELEMENT_NODE: {
      appendElement(node as Element, depth, lines);
      return;
    }
    default:
      return;
  }
}

function appendElement(element: Element, depth: number, lines: string[]): void {
  const pad = INDENT.repeat(depth);
  const open = `<${element.nodeName}${attributesOf(element)}`;
  const children = Array.from(element.childNodes).filter(isRenderable);
  const onlyChild = children.length === 1 ? children[0] : undefined;
  if (children.length === 0) {
    lines.push(`${pad}${open}/>`);
  } else if (onlyChild?.nodeType === Node.TEXT_NODE) {
    const text = escapeXmlText(onlyChild.nodeValue?.trim() ?? "");
    lines.push(`${pad}${open}>${text}</${element.nodeName}>`);
  } else {
    lines.push(`${pad}${open}>`);
    children.forEach((child) => appendNode(child, depth + 1, lines));
    lines.push(`${pad}</${element.nodeName}>`);
  }
}

function isRenderable(node: Node): boolean {
  return node.nodeType !== Node.TEXT_NODE || (node.nodeValue?.trim() ?? "") !== "";
}

function attributesOf(element: Element): string {
  return Array.from(element.attributes)
    .map((attribute) => ` ${attribute.name}="${escapeXmlAttribute(attribute.value)}"`)
    .join("");
}

function escapeXmlText(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeXmlAttribute(value: string): string {
  return escapeXmlText(value).replace(/"/g, "&quot;");
}

// A markup token is bounded by < and >, so each alternative scans forward once and never
// backtracks over the base64 attachment blobs that BR embeds in a response Melding.
const MARKUP = /<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<[?!/]?[^<>]*>/g;
const ATTRIBUTE = /(?<=\s)([^\s=<>/]+)="([^"]*)"/g;

export function highlightXml(xml: string): string {
  return replaceMatches(formatXml(xml), MARKUP, highlightMarkup);
}

function highlightMarkup(token: string): string {
  if (token.startsWith("<!--")) {
    return span("comment", escapeHtml(token));
  } else if (token.startsWith("<![CDATA[")) {
    return span("attributeValue", escapeHtml(token));
  } else if (token.startsWith("<?")) {
    return span("declaration", escapeHtml(token));
  } else {
    return replaceMatches(token, ATTRIBUTE, highlightAttribute, (text) =>
      span("tag", escapeHtml(text)),
    );
  }
}

function highlightAttribute(attribute: string, name: string, value: string): string {
  return (
    span("attributeName", escapeHtml(name)) +
    span("tag", "=") +
    span("attributeValue", `&quot;${escapeHtml(value)}&quot;`)
  );
}

function replaceMatches(
  input: string,
  pattern: RegExp,
  onMatch: (match: string, ...groups: string[]) => string,
  onGap: (text: string) => string = escapeHtml,
): string {
  const parts: string[] = [];
  let cursor = 0;
  Array.from(input.matchAll(pattern)).forEach((match) => {
    parts.push(onGap(input.slice(cursor, match.index)), onMatch(match[0], ...match.slice(1)));
    cursor = match.index + match[0].length;
  });
  parts.push(onGap(input.slice(cursor)));
  return parts.join("");
}

function span(color: keyof typeof colors, content: string): string {
  return `<span style="color:${colors[color]}">${content}</span>`;
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
